export const DOCS_CONTEXT = `
FrankieOne V2 API — Reference Data for Documentation:

====== V2 BASE URLs ======
UAT API:  https://api.uat.frankie.one/v2/
Prod API: https://api.frankie.one/v2/
UAT Portal:  https://portal.uat.frankie.one/login
Prod Portal: https://portal.frankie.one/login
Health check: GET /v2/kyc/ruok

====== AUTH HEADERS (V2) — SAME AS V1, CASE SENSITIVE ======
api_key                   REQUIRED  All lowercase with underscore
X-Frankie-CustomerID      REQUIRED  Uppercase ID at end — NOT CustomerId
X-Frankie-CustomerChildID OPTIONAL  Separate api_key required per child
Content-Type              REQUIRED  application/json

====== KEY V2 ENDPOINTS ======
POST   /v2/individuals                                                                              Create individual entity
GET    /v2/individuals/{entityId}                                                                    Retrieve individual
POST   /v2/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute     Execute workflow on existing individual
POST   /v2/individuals/new/serviceprofiles/{serviceName}/workflows/{workflowName}/execute            Create individual + execute workflow in one call
GET    /v2/workflows                                                                                 List available workflows
POST   /v2/organizations                                                                             Create organization entity
POST   /v2/organizations/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute   Execute workflow on organization
POST   /idv/v2/idvalidate/onboarding-url                                                            Generate hosted OneSDK URL (requires entity reference)

====== V2 PREDEFINED WORKFLOWS (AU) ======
AUS-Basic1V-IDOnly            Basic Gov ID check only (DVS)
AUS-Basic2V-TwoPlus           Standard 2+2 electronic verification (Blocklist + 2 sources)
AUS-Basic2V-TwoPlusID         2+2 + Gov ID verification
AUS-Basic2V-LowFriction       Low-friction, name + DOB only (~3s)
AUS-Basic3V-TwoPlus           3-source fallback electronic verification
AUS-Basic3V-TwoPlusID         3-source + Gov ID (high assurance)
AUS-Basic3V-AgeVerify          Age verification (18+)
AUS-Advanced1V-IDOnly         Enhanced ID + biometric verification
AUS-Advanced3V-TwoPlusID      3-source + Gov ID + biometrics (highest assurance)
AUS-Risk-CDD-Email-Phone      Risk-based CDD with email + phone signals

====== V2 PREDEFINED WORKFLOWS (GLOBAL / AML) ======
GLB-IntlOnePlus               International 1-source verification
GLB-AML-Advanced              Global AML: PEP (L1-4), Sanctions, Adverse Media
GLB-AML-Basic                 Global AML: PEP, Sanctions only
GLB-AgeVerify                 Global age verification (DOB only)

====== V2 ORGANIZATION WORKFLOWS ======
AUS-Organization-Profile      Organization profile lookup
AUS-Organization-Ownership    Organization ownership + UBO resolution (ASIC, directors, UBOs)
AUS-Organization-AML          Organization AML screening

====== V2 WORKFLOW RESPONSE ======
workflowResult.status           PASS | FAIL | REVIEW | CLEAR | COMPLETE | INCOMPLETE | ERROR | TIMEOUT
workflowResult.result           Overall outcome
workflowResult.riskAssessment   Risk level: LOW, MEDIUM, HIGH
workflowResult.workflowStepResults   Array of individual check/step results
workflowResult.issues           Specific issues found

====== V2 TEST DATA (UAT) ======
Same testbed personas as V1 — JAMES A TESTONE (PASS), DIANE NICHOLSON (PEP+Sanctions), etc.
V2 uses DRIVERS_LICENSE (American spelling), PASSPORT, NATIONAL_ID, VISA, IMMIGRATION, NATIONAL_HEALTH_ID, TAX_ID
Full test data: https://docs.frankieone.com/docs/uat-test-data

====== V2 KYB TEST ENTITIES (AUS) ======
SIMPLE:  FOX PTY. LTD. | ACN: 052121347 | ABN: 37052121347
MEDIUM:  BCP (PITT STREET) PTY LTD | ACN: 650193865
COMPLEX: CLARO DISABILITY SERVICES PTY LTD | ACN: 082554630
`;

export const SECTIONS = [
  // Part 1: Getting Started & Core Concepts
  { id: "overview",     title: "Overview & Getting Started",           emoji: "\u{1F680}", hint: "What is FrankieOne V2, environments, UAT vs Production, first API call" },
  { id: "auth",         title: "Authentication & Headers",             emoji: "\u{1F511}", hint: "Header format, case-sensitivity, code examples, error fixes", apiOnly: true },
  { id: "portal",       title: "Portal Access & Navigation",           emoji: "\u{1F5A5}\uFE0F", hint: "Requesting access, portal URLs, team management" },
  { id: "resources",    title: "Resources & Support",                  emoji: "\u{1F4DA}", hint: "V2 docs, AI assistant, Teams/Slack support channels" },
  { id: "int_diagrams", title: "Integration Diagrams",                 emoji: "\u{1F517}", hint: "Architecture overview, system integration patterns" },
  { id: "workflows",    title: "Workflows & Service Profiles",         emoji: "\u2699\uFE0F", hint: "Predefined workflows, naming convention, service profiles" },
  // Part 2: Verification Workflows (implementation + testing unified)
  { id: "kyc",          title: "Verifying Individuals (KYC)",          emoji: "\u{1F50D}", hint: "Create individual, execute workflow, interpret results, test with JAMES A TESTONE" },
  { id: "idv",          title: "Identity Document Verification (IDV)", emoji: "\u{1FAAA}", hint: "OneSDK, hosted flow, document capture, biometrics, test with sample ID" },
  { id: "kyb",          title: "Verifying Businesses (KYB)",           emoji: "\u{1F3E2}", hint: "Organization entity, UBO ownership, workflow execution, test with FOX PTY LTD" },
  { id: "aml",          title: "AML, PEP & Sanctions Screening",      emoji: "\u{1F6E1}\uFE0F", hint: "Screening workflows, resolution, monitoring, test with DIANE NICHOLSON" },
  { id: "risk",         title: "Risk Attributes & KVPs",              emoji: "\u{1F4CB}", hint: "Custom risk attributes via extraData key-value pairs, best practices" },
  { id: "sso",          title: "SSO (SAML) Setup",                    emoji: "\u{1F510}", hint: "SAML 2.0 SSO for portal, Okta/Azure config, attribute mapping" },
  // Part 3: Advanced & Reference
  { id: "webhooks",     title: "Webhooks & Notifications",             emoji: "\u{1F514}", hint: "Webhook setup, event types, workflow results, retry logic", apiOnly: true },
  { id: "diagrams",     title: "Sequence Diagrams",                    emoji: "\u{1F4CA}", hint: "KYC, KYB, IDV, and AML call flows", apiOnly: true },
];

export const STATUS_CONFIG = {
  approved:   { color: "#4ecca3", icon: "\u2713", label: "Approved" },
  ready:      { color: "#f0c040", icon: "\u25CF", label: "Review" },
  generating: { color: "#74b9ff", icon: "\u27F3", label: "Working..." },
  error:      { color: "#ff6b6b", icon: "\u2717", label: "Error" },
  undefined:  { color: "#555",    icon: "\u25CB", label: "Pending" },
};
